<template>
  <div class="fss-page">

    <!-- HERO -->
    <section class="fss-hero">
      <div class="fss-hero-inner">
        <div class="fss-hero-text">
          <p class="fss-eyebrow">Programs</p>
          <h1 class="fss-h1">Future STEM Stars.</h1>
          <p class="fss-hero-body">
            In partnership with <a class="fss-hero-link" href="https://katwiganddale.com/" target="_blank">Katwig &amp; Dale</a>, 
            we walk into Nigerian public secondary schools and talk to young girls about 
            careers in STEM — before anyone has had the chance to tell them it isn't for them.
          </p>
          <div class="fss-hero-actions">
            <a class="fss-btn" href="mailto:partnerships@nexascale.org">
              Bring us to your school →
            </a>
            <a class="fss-btn fss-btn--ghost" href="mailto:partnerships@nexascale.org">
              Volunteer →
            </a>
          </div>
        </div>
        <div class="fss-hero-image">
          <img src="../../assets/images/programs/stem-stars-min.jpg" alt="Future STEM Stars" />
        </div>
      </div>
    </section>

    <!-- PULL QUOTE -->
    <div class="fss-quote-strip">
      <div class="fss-quote-inner">
        <p class="fss-quote">
          "We show up before they've been told tech isn't for them."
        </p>
      </div>
    </div>

    <!-- SCHOOL PHOTO — full bleed -->
    <div class="fss-photo-break">
      <img src="../../assets/images/programs/fss.jpeg" alt="Future STEM Stars in school" class="fss-photo-img" />
      <div class="fss-photo-caption">
        A Future STEM Stars session in a Nigerian public secondary school.
      </div>
    </div>

    <!-- CONTENT -->
    <section class="fss-content">
      <div class="fss-content-inner">

        <div class="fss-section">
          <h2 class="fss-section-title">Why this exists</h2>
          <div class="fss-section-body">
            <p>
              By the time most girls in Nigerian secondary schools are asked what they want 
              to be, the answer "software engineer" or "AI researcher" isn't on the table — 
              not because they lack the ability, but because no one put it there.
            </p>
            <p>
              Future STEM Stars exists to change that conversation at the point where it matters 
              most — secondary school, ages 11 to 18, before the options narrow and the self-doubt 
              sets in. We go to the schools. We meet the girls where they are. We make the 
              possibilities real and concrete.
            </p>
            <p>
              Every session we run is a bet that the next generation of African women in tech 
              is already sitting in a classroom somewhere, waiting for someone to show up.
            </p>
          </div>
        </div>

        <div class="fss-section">
          <h2 class="fss-section-title">What we do</h2>
          <div class="fss-section-body">
            <p>
              We don't just give talks. We build an ecosystem of support around the girls 
              we reach — from the first school visit to mentorship that continues long 
              after we leave.
            </p>
            <div class="fss-activities">
              <div class="fss-activity">
                <span class="fss-activity-num">01</span>
                <div>
                  <span class="fss-activity-name">School visits &amp; STEM sessions</span>
                  <p>We visit secondary schools to run structured sessions on STEM careers — 
                  what they are, what they pay, what the path looks like, and who is already 
                  doing it. We bring role models who look like the girls in the room.</p>
                </div>
              </div>
              <div class="fss-activity">
                <span class="fss-activity-num">02</span>
                <div>
                  <span class="fss-activity-name">AI education curriculum</span>
                  <p>We deliver structured AI and technology education directly inside schools — 
                  practical, age-appropriate, and designed to spark curiosity rather than 
                  overwhelm. Girls leave sessions knowing more about AI than most adults around them.</p>
                </div>
              </div>
              <div class="fss-activity">
                <span class="fss-activity-num">03</span>
                <div>
                  <span class="fss-activity-name">Mentorship</span>
                  <p>With parental consent, girls who want to go further are connected to 
                  experienced women in tech who volunteer as mentors. These relationships 
                  extend well beyond the classroom and give girls ongoing guidance as they 
                  grow into their careers.</p>
                </div>
              </div>
              <div class="fss-activity">
                <span class="fss-activity-num">04</span>
                <div>
                  <span class="fss-activity-name">Community</span>
                  <p>We're building a peer network where girls interested in STEM can connect 
                  with each other, share progress, celebrate wins, and know they're not alone 
                  in choosing this path.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="fss-section">
          <h2 class="fss-section-title">Who this is for</h2>
          <div class="fss-section-body">
            <p>
              Girls in Nigerian public secondary schools between the ages of 11 and 18. 
              That's the window. That's where we focus.
            </p>
            <p>
              We chose this age range deliberately. It's the period when interest in STEM 
              either takes root or gets quietly discouraged. The earlier we show up, the 
              better the odds that a girl chooses a path she genuinely wants — not one 
              she defaulted to because no one showed her the alternative.
            </p>
          </div>
        </div>

        <div class="fss-section-split">
          <div class="fss-split-col">
            <h2 class="fss-split-title">Is your school interested?</h2>
            <p>
              If you're a school administrator, teacher, or parent who wants to bring 
              Future STEM Stars to your school, we want to hear from you. We work with 
              schools directly to plan sessions, coordinate with teachers, and make sure 
              the visits are meaningful — not just a one-off talk.
            </p>
            <a class="fss-link" href="mailto:partnerships@nexascale.org">Reach out to us →</a>
          </div>
          <div class="fss-split-col">
            <h2 class="fss-split-title">Want to volunteer?</h2>
            <p>
              We're always looking for women in tech who want to give back by mentoring, 
              speaking at sessions, or helping shape the curriculum. If that's you — 
              even if you're early in your career — we'd love to talk.
            </p>
            <a class="fss-link" href="mailto:partnerships@nexascale.org">Get involved →</a>
          </div>
        </div>

      </div>
    </section>

  </div>
</template>

<style scoped>
.fss-page {
  min-height: var(--pageheight, 100vh);
  margin-top: 80px;
  text-align: left;
}
.fss-page * { box-sizing: border-box; }
.fss-page p, .fss-page h1, .fss-page h2 { text-align: left; }

/* HERO */
.fss-hero {
  background: #191616;
  padding: 80px 7%;
}
.fss-hero-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 420px;
  gap: 72px;
  align-items: center;
}
.fss-eyebrow {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #FFB812;
  display: block;
  margin-bottom: 16px;
}
.fss-h1 {
  font-size: clamp(2.2rem, 5vw, 4rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.05;
  color: #ffffff;
  margin: 0 0 20px;
  word-break: break-word;
}
.fss-hero-body {
  font-size: 0.97rem;
  line-height: 1.8;
  color: rgba(255,255,255,0.55);
  margin: 0 0 32px;
  max-width: 520px;
}
.fss-hero-link {
  color: #FFB812;
  text-decoration: underline;
  text-decoration-color: rgba(255,184,18,0.4);
}
.fss-hero-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
.fss-btn {
  display: inline-block;
  background: #FFB812;
  color: #191616;
  font-weight: 700;
  font-size: 0.9rem;
  padding: 12px 26px;
  border-radius: 8px;
  text-decoration: none;
  transition: background 0.2s;
}
.fss-btn:hover { background: #FF9914; }
.fss-btn--ghost {
  background: transparent;
  color: rgba(255,255,255,0.7);
  border: 1.5px solid rgba(255,255,255,0.2);
}
.fss-btn--ghost:hover {
  border-color: rgba(255,255,255,0.5);
  color: #ffffff;
}
.fss-hero-image { border-radius: 16px; overflow: hidden; }
.fss-hero-image img {
  width: 100%;
  display: block;
  border-radius: 16px;
  object-fit: cover;
  object-position: center top;
  max-height: 480px;
}

/* QUOTE STRIP */
.fss-quote-strip {
  background: #FFB812;
  padding: 40px 7%;
}
.fss-quote-inner {
  max-width: 1200px;
  margin: 0 auto;
}
.fss-quote {
  font-size: clamp(1.2rem, 2.5vw, 1.8rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #191616;
  margin: 0;
  line-height: 1.3;
}

/* PHOTO BREAK */
.fss-photo-break {
  position: relative;
  width: 100%;
}
.fss-photo-img {
  width: 100%;
  height: 520px;
  object-fit: cover;
  object-position: center 40%;
  display: block;
}
.fss-photo-caption {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(25,22,22,0.7));
  color: rgba(255,255,255,0.7);
  font-size: 0.75rem;
  font-weight: 500;
  padding: 32px 7% 16px;
  letter-spacing: 0.04em;
}

/* CONTENT */
.fss-content { background: #ffffff; }
.fss-content-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 7%;
}

.fss-section {
  padding: 72px 0;
  border-bottom: 1px solid #e8e4e0;
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 60px;
  align-items: start;
}
.fss-section:last-child { border-bottom: none; }

/* SPLIT SECTION */
.fss-section-split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;
  background: #FFFBEB;
  margin: 0 -7%;
  padding: 72px 7%;
  border-top: 1px solid #e8e4e0;
}
.fss-split-col {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.fss-split-title {
  font-size: 1.2rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: #191616;
  margin: 0;
}
.fss-split-col p {
  font-size: 0.97rem;
  line-height: 1.8;
  color: #555250;
  margin: 0;
}

.fss-section-title {
  font-size: 1.2rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: #191616;
  margin: 0;
  position: sticky;
  top: 100px;
  word-break: break-word;
}

.fss-section-body p {
  font-size: 0.97rem;
  line-height: 1.8;
  color: #555250;
  margin: 0 0 16px;
}
.fss-section-body p:last-of-type { margin-bottom: 0; }

.fss-link {
  display: inline-block;
  font-size: 0.88rem;
  font-weight: 700;
  color: #191616;
  text-decoration: none;
  border-bottom: 2px solid #FFB812;
  padding-bottom: 2px;
  transition: color 0.2s;
}
.fss-link:hover { color: #FF9914; }

/* ACTIVITIES */
.fss-activities {
  display: flex;
  flex-direction: column;
  border-top: 1px solid #e8e4e0;
  margin-top: 24px;
}
.fss-activity {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  padding: 24px 0;
  border-bottom: 1px solid #e8e4e0;
}
.fss-activity:last-child { border-bottom: none; }
.fss-activity-num {
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  color: #FFB812;
  flex-shrink: 0;
  padding-top: 4px;
  width: 20px;
}
.fss-activity-name {
  display: block;
  font-size: 0.95rem;
  font-weight: 700;
  color: #191616;
  margin-bottom: 8px;
}
.fss-activity p {
  font-size: 0.88rem !important;
  line-height: 1.7 !important;
  color: #666260 !important;
  margin: 0 !important;
}

/* RESPONSIVE */
@media (max-width: 900px) {
  .fss-hero-inner { grid-template-columns: 1fr; gap: 40px; }
  .fss-hero-image { order: -1; }
  .fss-hero-image img { max-height: 280px; }
  .fss-photo-img { height: 300px; }
  .fss-section { grid-template-columns: 1fr; gap: 20px; padding: 52px 0; }
  .fss-section-split { grid-template-columns: 1fr; padding: 52px 5%; margin: 0 -5%; }
  .fss-section-title { position: static; }
  .fss-hero { padding: 60px 5%; }
  .fss-quote-strip { padding: 32px 5%; }
  .fss-content-inner { padding: 0 5%; }
}
</style>